/**
 * Keeps crawlers and automated browsers out of Google Analytics.
 *
 * Rendering crawlers (PetalBot, Bytespider, cloud-hosted headless fleets) run
 * the page's JavaScript, so without this gate each page they render shows up
 * as a GA4 user. Oct 2026 server logs: almost all "Singapore" visitors were
 * PetalBot, Bytespider and a Tencent Cloud fleet sending Chrome 101–133 and
 * iOS 13 user agents years after those versions shipped.
 *
 * Only analytics is skipped. The page itself works the same for everyone.
 */

const AUTOMATION_UA = /bot\b|bot\/|crawl|spider|slurp|bytespider|petalbot|headlesschrome|lighthouse|pagespeed|chrome-lighthouse|ptst\/|phantomjs|puppeteer|playwright|selenium|python|curl\/|wget\/|go-http-client|axios\/|node-fetch|okhttp|java\/|reactsnap/i;

// Chrome ships a new major every four weeks; 130 shipped on 2024-10-15.
const CHROME_REFERENCE_MAJOR = 130;
const CHROME_REFERENCE_DATE = Date.UTC(2024, 9, 15);
const CHROME_RELEASE_DAYS = 28;
// Majors this far behind the expected current one are treated as spoofed.
// About 20 months: real browsers auto-update long before that.
export const STALE_CHROME_MAJORS = 22;
// iOS versions this old no longer run current Safari; fleets keep sending them.
export const MIN_IOS_MAJOR = 15;

export function expectedChromeMajor(now = Date.now()) {
  const releases = Math.floor((now - CHROME_REFERENCE_DATE) / (CHROME_RELEASE_DAYS * 24 * 60 * 60 * 1000));
  return CHROME_REFERENCE_MAJOR + Math.max(0, releases);
}

export function isAutomatedUserAgent(userAgent, now = Date.now()) {
  const ua = String(userAgent || '');
  if (!ua) return true;
  if (AUTOMATION_UA.test(ua)) return true;

  const chrome = /(?:Chrome|CriOS)\/(\d+)\./.exec(ua);
  if (chrome && Number(chrome[1]) < expectedChromeMajor(now) - STALE_CHROME_MAJORS) return true;

  const ios = /(?:iPhone|CPU) OS (\d+)_/.exec(ua);
  if (ios && Number(ios[1]) < MIN_IOS_MAJOR) return true;

  return false;
}

/** True when this browser should not load Google Analytics. */
export function isAutomatedVisitor(nav = typeof navigator !== 'undefined' ? navigator : null, now = Date.now()) {
  if (!nav) return true;
  if (nav.webdriver === true) return true;
  return isAutomatedUserAgent(nav.userAgent, now);
}
